import { create } from 'zustand'
import { onWsEvent } from './websocket'
import type { Message } from '@/api/messaging-types'

interface MessagingState {
  activeConversationId: number | null
  totalUnread: number
  typingUsers: Record<number, number[]> // conversation_id -> user_ids
  onlineUsers: Set<number>
  replyingTo: Message | null

  setActiveConversation: (id: number | null) => void
  setTotalUnread: (count: number) => void
  setReplyingTo: (msg: Message | null) => void
}

export const useMessagingStore = create<MessagingState>((set) => ({
  activeConversationId: null,
  totalUnread: 0,
  typingUsers: {},
  onlineUsers: new Set(),
  replyingTo: null,

  setActiveConversation: (id) => set({ activeConversationId: id, replyingTo: null }),
  setTotalUnread: (count) => set({ totalUnread: count }),
  setReplyingTo: (msg) => set({ replyingTo: msg }),
}))

// ── WebSocket event handlers ────────────────────────────────────

onWsEvent((event) => {
  const type = event.type as string
  const store = useMessagingStore.getState()

  switch (type) {
    case 'message.new': {
      const data = event as Record<string, unknown>
      const convId = data.conversation_id as number

      // Update unread count (will be synced on next query refetch)
      if (convId !== store.activeConversationId) {
        useMessagingStore.setState({ totalUnread: store.totalUnread + 1 })
      }

      // Clear typing for this sender
      const message = data.message as Record<string, unknown>
      if (message?.sender_id) {
        const senderId = message.sender_id as number
        const typing = { ...store.typingUsers }
        if (typing[convId]) {
          typing[convId] = typing[convId].filter(id => id !== senderId)
          useMessagingStore.setState({ typingUsers: typing })
        }
      }
      break
    }

    case 'typing': {
      const data = event as Record<string, unknown>
      const convId = data.conversation_id as number
      const userId = data.user_id as number
      const isTyping = data.is_typing as boolean

      const typing = { ...store.typingUsers }
      const current = typing[convId] || []

      if (isTyping && !current.includes(userId)) {
        typing[convId] = [...current, userId]
      } else if (!isTyping) {
        typing[convId] = current.filter(id => id !== userId)
      }

      useMessagingStore.setState({ typingUsers: typing })

      // Auto-clear after 5 seconds (in case stop event is missed)
      if (isTyping) {
        setTimeout(() => {
          const s = useMessagingStore.getState()
          const t = { ...s.typingUsers }
          if (t[convId]) {
            t[convId] = t[convId].filter(id => id !== userId)
            useMessagingStore.setState({ typingUsers: t })
          }
        }, 5000)
      }
      break
    }

    case 'presence.update': {
      const data = event as Record<string, unknown>
      const userId = data.user_id as number
      const status = data.status as string
      const online = new Set(store.onlineUsers)

      if (status === 'online') {
        online.add(userId)
      } else {
        online.delete(userId)
      }
      useMessagingStore.setState({ onlineUsers: online })
      break
    }

    case 'unread.update': {
      const data = event as Record<string, unknown>
      if (typeof data.total === 'number') {
        useMessagingStore.setState({ totalUnread: data.total as number })
      }
      break
    }
  }
})
